/**
 * #315 item 5: the apps analysed by upload (GET /v1/discovery/mobile-apps),
 * newest first, with what each one's last scan read and its open tracker and
 * permission findings, and how many of those have no purpose declared in the
 * purpose register. The controller limits the list to the caller's units.
 */
import { useEffect, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CardDescription, CardHeader, CardTitle, GlassCard } from '@/components/ui/card';
import { listMobileApps, type AnalysedMobileApp } from '@/lib/api';

export const ANALYSED_APPS_NOTE =
  'Each app as its last upload was scanned. A finding with no declared purpose has neither mobile-sdk:<id> nor ' +
  'mobile-permission:<permission> (or the app-specific mobile-sdk:<package>:<id>) in the purpose register, source type application; ' +
  'declaring one lowers that finding to low on the next upload.';

export function AnalysedMobileApps() {
  const [apps, setApps] = useState<AnalysedMobileApp[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function load(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      setApps((await listMobileApps()).apps);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The list could not be loaded.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <GlassCard className="p-5 mb-6" data-testid="analysed-mobile-apps">
      <CardHeader className="p-0 mb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base">Analysed mobile apps</CardTitle>
          <Button size="sm" variant="glass" onClick={() => void load()} disabled={loading} aria-label="Refresh the list">
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          </Button>
        </div>
        <CardDescription>{ANALYSED_APPS_NOTE}</CardDescription>
      </CardHeader>
      {error && (
        <div className="text-xs text-sev-high" role="alert">
          {error}
        </div>
      )}
      {apps !== null && apps.length === 0 && !error && <div className="text-xs text-muted-foreground">No app has been analysed yet.</div>}
      {apps !== null && apps.length > 0 && (
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left py-1">App</th>
              <th className="text-left py-1">Last scan</th>
              <th className="text-right py-1">Trackers</th>
              <th className="text-right py-1">Open permissions</th>
              <th className="text-right py-1">No purpose</th>
            </tr>
          </thead>
          <tbody>
            {apps.map((a) => (
              <tr key={a.assetId} className="border-t border-border/30" data-testid={`analysed-app-${a.assetId}`}>
                <td className="py-1">
                  <div className="font-mono">{a.packageName ?? a.assetExternalRef}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {a.platform ?? 'unknown'}
                    {a.versionCode !== null ? `, version ${a.versionCode}` : ''}
                    {a.signerVerified ? ', signature verified' : ', signature not verified'}
                  </div>
                </td>
                <td className="py-1">
                  <div>{new Date(a.analysedAt).toLocaleString()}</div>
                  <Badge variant={a.coverageComplete ? 'success' : 'warning'} className="text-[9px]">
                    {a.coverageComplete ? 'complete' : 'not complete'}
                  </Badge>
                </td>
                <td className="py-1 text-right">
                  {a.openTrackers} open ({a.trackersInLastScan} in last scan)
                </td>
                <td className="py-1 text-right">{a.openPermissions}</td>
                <td className={`py-1 text-right ${a.openWithoutPurpose > 0 ? 'text-warning' : ''}`}>{a.openWithoutPurpose}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </GlassCard>
  );
}
